// Web Push helpers — service-worker registration + subscribe/unsubscribe.
// Uses the shared axios `api` instance so JWT auth + backend URL are consistent.
import { api } from "./api";

const DECLINED_KEY = "pstr_push_declined";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushDeclined() {
  return localStorage.getItem(DECLINED_KEY) === "1" || Notification?.permission === "denied";
}

export function markDeclined() {
  localStorage.setItem(DECLINED_KEY, "1");
}

// Registers the service worker once. Safe to call on every load.
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(
      `${process.env.PUBLIC_URL || ""}/service-worker.js`
    );
  } catch (e) {
    return null;
  }
}

// Returns "granted" | "denied" | "default" | "unsupported"
export function notificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export async function isSubscribed() {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

// Must be called from a user gesture. Requests permission, subscribes, and
// stores the subscription server-side. Throws on failure.
export async function enablePush() {
  if (!pushSupported()) throw new Error("Notifications aren't supported on this device.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    markDeclined();
    throw new Error("Notification permission was not granted.");
  }
  localStorage.removeItem(DECLINED_KEY);

  const { data } = await api.get("/push/public-key");
  if (!data.configured || !data.publicKey) throw new Error("Push is not configured on the server.");

  const reg = (await navigator.serviceWorker.ready) || (await registerServiceWorker());
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    tz_offset_minutes: new Date().getTimezoneOffset(),
  });
  return true;
}

export async function disablePush() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      await api.delete("/push/subscribe", { data: { endpoint: json.endpoint } });
      await sub.unsubscribe();
    }
  } catch {
    /* ignore */
  }
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}
